/**
 * Base local — Dexie / IndexedDB.
 *
 * IndexedDB es la ÚNICA fuente de verdad de la interfaz. La sincronización
 * (Fase 8) nunca está en el camino de lectura.
 *
 * ÍNDICES: `dirty` y `deleted` son booleanos y NO van en ninguna cadena de
 * índices. IndexedDB no indexa booleanos de forma confiable. Se filtran en
 * memoria en el repositorio, que a este volumen —decenas o pocos cientos de
 * filas— no cuesta nada.
 */

import Dexie, { type EntityTable } from 'dexie';

import type { Ajustes } from '../domain/modelo/ajustes.js';
import type { ElementoBanco } from '../domain/modelo/banco.js';
import type { Grabacion } from '../domain/modelo/grabacion.js';
import type { SesionRegistrada } from '../domain/modelo/sesion.js';

export const NOMBRE_BASE = 'EnglishTrainingHubDB';

/** Los ajustes son un singleton: se les añade una clave solo en almacenamiento. */
export interface AjustesAlmacenados extends Ajustes {
  id: string;
}

/**
 * Tablas de la aplicación.
 *
 * Lista canónica y explícita. Nunca recorrer `db.tables` para operaciones
 * masivas: cuando llegue el addon de sincronización, esa colección incluirá
 * tablas internas suyas, y vaciarlas destruiría el estado de sincronización.
 */
export const TABLAS_DE_LA_APLICACION = [
  'sesiones',
  'banco',
  'grabaciones',
  'ajustes',
] as const;

export type NombreTabla = (typeof TABLAS_DE_LA_APLICACION)[number];

export class BaseLocal extends Dexie {
  sesiones!: EntityTable<SesionRegistrada, 'id'>;
  banco!: EntityTable<ElementoBanco, 'id'>;
  grabaciones!: EntityTable<Grabacion, 'id'>;
  ajustes!: EntityTable<AjustesAlmacenados, 'id'>;

  constructor(nombre: string = NOMBRE_BASE) {
    super(nombre);

    this.version(1).stores({
      // `estado`, `tipo`, `semana` y `sessionKey` se consultan de verdad.
      // `dirty` y `deleted` NO: booleanos, se filtran en memoria.
      sesiones: 'id, tipo, sessionKey, semana, estado, iniciadaEn, updatedAt',
      banco: 'id, kind, area, estado, prioridad, creadoEn, ultimaPracticaEn, updatedAt',
      grabaciones: 'id, sesionId, stepKey, etiqueta, creadoEn, updatedAt',
      ajustes: 'id',
    });
  }
}

export const db = new BaseLocal();

/**
 * Error de escritura, para que un fallo de persistencia sea VISIBLE.
 *
 * «Los writes offline no desaparecen en silencio» (CA-8.1) empieza aquí: si una
 * escritura falla, la aplicación tiene que poder decirlo, no tragárselo.
 */
export class EscrituraFallidaError extends Error {
  constructor(
    public readonly operacion: string,
    public override readonly cause?: unknown,
  ) {
    super(`No se pudo guardar: ${operacion}`);
    this.name = 'EscrituraFallidaError';
  }
}

/** Envuelve una escritura para que su fallo sea identificable y visible. */
export async function escribir<T>(operacion: string, fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    throw new EscrituraFallidaError(operacion, error);
  }
}
